/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Reporting", description = "Review engagement in the app and query the event tables Hightouch writes to your warehouse.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Reporting",
        description: "Review engagement in the app and query the event tables Hightouch writes to your warehouse.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Review a message's performance",
          "depth": 0,
          "id": "review-a-messages-performance"
        }, {
          "value": "See who didn't receive a message",
          "depth": 0,
          "id": "see-who-didnt-receive-a-message"
        }, {
          "value": "Query the events in your warehouse",
          "depth": 0,
          "id": "query-the-events-in-your-warehouse"
        }, {
          "value": "Table schemas",
          "depth": 0,
          "id": "table-schemas"
        }, {
          "value": "Dispatcher events",
          "depth": 1,
          "id": "dispatcher-events"
        }, {
          "value": "Deliverability events",
          "depth": 1,
          "id": "deliverability-events"
        }, {
          "value": "Engagement events",
          "depth": 1,
          "id": "engagement-events"
        }, {
          "value": "Consent events",
          "depth": 1,
          "id": "consent-events"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Why is a column missing from my table?",
          "depth": 1,
          "id": "why-is-a-column-missing-from-my-table"
        }, {
          "value": "Why do I see columns that aren't listed here?",
          "depth": 1,
          "id": "why-do-i-see-columns-that-arent-listed-here"
        }, {
          "value": "Which timestamp should I use?",
          "depth": 1,
          "id": "which-timestamp-should-i-use"
        }, {
          "value": "SMS reporting",
          "depth": 0,
          "id": "sms-reporting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      code: "code",
      h3: "h3",
      a: "a",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams, data teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Native Delivery reports on sends in the app and writes every event back to your warehouse, so you can build your own audiences and reports on top of them."
        })
      }), "\n", _jsx(_components.h2, {
        id: "review-a-messages-performance",
        children: "Review a message's performance"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Engagement"
        }), " card on a message or campaign shows three metrics:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Metric"
            }), _jsx(_components.th, {
              children: "What it counts"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sent"
              })
            }), _jsx(_components.td, {
              children: "Messages handed off for delivery."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Opened"
              })
            }), _jsx(_components.td, {
              children: "Share of messages opened, counted once per recipient."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clicked"
              })
            }), _jsx(_components.td, {
              children: "Share of messages with a link clicked, counted once per recipient."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Opens and clicks here exclude machine and bot activity, including the prefetching that security scanners and mail privacy features perform. Each recipient counts once, so these measure how many people engaged, not how many times."
      }), "\n", _jsx(_components.p, {
        children: "Three things mark activity as machine-generated: a known security-scanner signature, engagement within two seconds of the send, and the mail provider's own machine-open signal. That last one catches privacy features that load images automatically. Opens through Gmail's and Yahoo's image proxies are counted as real, because a person did open the message even though the request comes from the provider's servers."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "These numbers are lower than the raw counts other platforms report, which usually include bot opens and count repeats. Compare your own trends over time instead of matching rates across platforms."
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Engagement over time"
        }), " charts the same three metrics, and the ", _jsx(_components.strong, {
          children: "Link clicks"
        }), " table breaks clicks down by URL with unique and total counts."]
      }), "\n", _jsx(_components.h2, {
        id: "see-who-didnt-receive-a-message",
        children: "See who didn't receive a message"
      }), "\n", _jsx(_components.p, {
        children: "Recipients Hightouch withheld a message from are reported with a reason for each:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Reason"
            }), _jsx(_components.th, {
              children: "What happened"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Not subscribed to topic"
              })
            }), _jsx(_components.td, {
              children: "Unsubscribed from the topic, or never confirmed an opt-in."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Channel unsubscribed"
              })
            }), _jsx(_components.td, {
              children: "Opted out of all email."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Previously bounced"
              })
            }), _jsx(_components.td, {
              children: "A previous email to this address bounced."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Suppressed"
              })
            }), _jsx(_components.td, {
              children: "A previous spam complaint, or a suppression rule."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Invalid recipient"
              })
            }), _jsx(_components.td, {
              children: "The address isn't a usable email address, so nothing was sent. Different from a bounce, where an address looked valid and delivery failed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Quiet hours"
              })
            }), _jsx(_components.td, {
              children: "The send fell in your quiet hours window."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Repeat send"
              })
            }), _jsx(_components.td, {
              children: "Already received this message, and repeats aren't allowed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User frequency limit"
              })
            }), _jsx(_components.td, {
              children: "Already at their message limit for the period."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Volume cap"
              })
            }), _jsx(_components.td, {
              children: "The message hit its total cap."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Channel daily cap"
              })
            }), _jsx(_components.td, {
              children: "The workspace hit its daily limit for email."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Expired"
              })
            }), _jsx(_components.td, {
              children: "A send with a Duration window didn't reach them inside it."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["These reasons explain a send that reached fewer people than expected. A high ", _jsx(_components.strong, {
          children: "Not subscribed to topic"
        }), " count points at consent. A high ", _jsx(_components.strong, {
          children: "Previously bounced"
        }), " count points at list quality."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A bounced address is suppressed permanently. There's no retry window. An address that bounced once won't be tried again."
        })
      }), "\n", _jsx(_components.h2, {
        id: "query-the-events-in-your-warehouse",
        children: "Query the events in your warehouse"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch writes five event tables into your warehouse, each suffixed with your workspace ID. Every row is one event tied to a recipient and a message."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "What it records"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cep_dispatcher_events"
              })
            }), _jsx(_components.td, {
              children: "What Hightouch attempted: sent, dropped, and failed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cep_deliverability_events"
              })
            }), _jsx(_components.td, {
              children: "Delivery outcomes: delivered and bounced."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cep_engagement_events"
              })
            }), _jsx(_components.td, {
              children: "Opens, clicks with the clicked URL, and spam reports."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cep_consent_events"
              })
            }), _jsx(_components.td, {
              children: "Subscription changes by topic."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Events land in your warehouse within about 15 minutes."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Managed models sit on top of these tables, already filtered and labeled, one per event type: Email delivered, Email opened, Email clicked, Email bounced, Email unsubscribed. Each is keyed on your parent model's primary key, so you can build an audience like \"opened an email in the last 30 days\" without writing SQL."
        })
      }), "\n", _jsx(_components.p, {
        children: "Query the raw tables directly for custom analysis, your own dashboards, or anything the managed models don't cover."
      }), "\n", _jsx(_components.h2, {
        id: "table-schemas",
        children: "Table schemas"
      }), "\n", _jsxs(_components.p, {
        children: ["Every table carries the same identity and timestamp columns, listed with each table below. Use ", _jsx(_components.code, {
          children: "timestamp"
        }), " for time-based analysis, ", _jsx(_components.code, {
          children: "provider_event_type"
        }), " to filter to a specific event, and ", _jsx(_components.code, {
          children: "parent_model_pk"
        }), " to join back to your customer data."]
      }), "\n", _jsx(_components.h3, {
        id: "dispatcher-events",
        children: "Dispatcher events"
      }), "\n", _jsx(_components.p, {
        children: "What Hightouch attempted to send, written at send time. One row per recipient per send attempt."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsx(_components.td, {
              children: "Unique row identifier, for de-duplicating"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "When the event happened. Use this for time filters"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "provider_event_type"
              })
            }), _jsxs(_components.td, {
              children: ["The event, such as ", _jsx(_components.code, {
                children: "delivered"
              }), ", ", _jsx(_components.code, {
                children: "bounce"
              }), ", ", _jsx(_components.code, {
                children: "open"
              }), ", ", _jsx(_components.code, {
                children: "click"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "channel_type"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "email"
              }), " or ", _jsx(_components.code, {
                children: "sms"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "parent_model_pk"
              })
            }), _jsx(_components.td, {
              children: "The recipient's primary key from your parent model"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_handle"
              })
            }), _jsx(_components.td, {
              children: "The recipient's email address or phone number"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: "The message this event belongs to"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_execution_id"
              })
            }), _jsx(_components.td, {
              children: "The specific send this event belongs to"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsx(_components.td, {
              children: "The campaign. Empty for journey sends"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_run_id"
              })
            }), _jsx(_components.td, {
              children: "The campaign run. Empty for journey sends"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsx(_components.td, {
              children: "The name of the message"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_id"
              })
            }), _jsx(_components.td, {
              children: "The journey. Empty for messages sent from a campaign"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_name"
              })
            }), _jsx(_components.td, {
              children: "The name of that journey"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "subject"
              })
            }), _jsx(_components.td, {
              children: "The rendered subject line this recipient received"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "preheader"
              })
            }), _jsx(_components.td, {
              children: "The rendered preview text"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "from_email"
              })
            }), _jsx(_components.td, {
              children: "The address the message was sent from"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "from_name"
              })
            }), _jsx(_components.td, {
              children: "The display name the message was sent from"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "drop_reason"
              })
            }), _jsx(_components.td, {
              children: "Why the recipient was skipped. Only set on dropped rows"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.h3, {
        id: "deliverability-events",
        children: [_jsx(_components.a, {
          href: "/native-delivery/deliverability",
          children: "Deliverability"
        }), " events"]
      }), "\n", _jsx(_components.p, {
        children: "What happened after handoff: whether the message reached the recipient's mail server."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "reason"
              })
            }), _jsx(_components.td, {
              children: "Why the message bounced or was dropped"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "response"
              })
            }), _jsx(_components.td, {
              children: "The receiving mail server's response"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bounce_classification"
              })
            }), _jsx(_components.td, {
              children: "Bounce category, such as invalid address or reputation"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "status"
              })
            }), _jsx(_components.td, {
              children: "SMTP status code for the attempt"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "attempt"
              })
            }), _jsx(_components.td, {
              children: "Which retry attempt this was"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "domain"
              })
            }), _jsx(_components.td, {
              children: "Recipient domain, useful for per-provider analysis"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "engagement-events",
        children: "Engagement events"
      }), "\n", _jsx(_components.p, {
        children: "What recipients did with the message: opens and clicks."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsx(_components.td, {
              children: "The exact URL clicked. Empty on opens"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url_normalized"
              })
            }), _jsx(_components.td, {
              children: "Same URL with tracking parameters stripped, better for grouping"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "useragent"
              })
            }), _jsx(_components.td, {
              children: "Mail client or browser that generated the open or click"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "inferred_tz"
              })
            }), _jsx(_components.td, {
              children: "Recipient timezone, where it could be determined"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "consent-events",
        children: "Consent events"
      }), "\n", _jsx(_components.p, {
        children: "Every change to a recipient's subscription preferences. Append-only, which preserves the full history."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "topic_id"
              })
            }), _jsx(_components.td, {
              children: "The subscription topic. Empty means the change applied to the whole channel"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "consent_status"
              })
            }), _jsxs(_components.td, {
              children: ["The resulting state, ", _jsx(_components.code, {
                children: "subscribed"
              }), " or ", _jsx(_components.code, {
                children: "unsubscribed"
              }), ". Read this one"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "updated_by"
              })
            }), _jsx(_components.td, {
              children: "Who made the change"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "updated_by_source"
              })
            }), _jsx(_components.td, {
              children: "Where it came from, such as the preference page or a one-click unsubscribe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "why-is-a-column-missing-from-my-table",
        children: "Why is a column missing from my table?"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch creates a column the first time an event carries a value for it. A workspace that has never recorded a particular event won't have its columns yet, and they appear once the first matching event arrives."
      }), "\n", _jsx(_components.h3, {
        id: "why-do-i-see-columns-that-arent-listed-here",
        children: "Why do I see columns that aren't listed here?"
      }), "\n", _jsx(_components.p, {
        children: "These tables aren't a fixed schema. Additional detail reported during delivery is written as extra columns. Your tables may hold more than what's documented above."
      }), "\n", _jsx(_components.h3, {
        id: "which-timestamp-should-i-use",
        children: "Which timestamp should I use?"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.code, {
          children: "timestamp"
        }), ". The other timestamp columns record when the event moved through each stage of collection, and are mainly useful for diagnosing delays."]
      }), "\n", _jsx(_components.h2, {
        id: "sms-reporting",
        children: "SMS reporting"
      }), "\n", _jsx(_components.p, {
        children: "SMS reports on sends and clicks. There's no open tracking for text messages."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Metric"
            }), _jsx(_components.th, {
              children: "What it counts"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sent"
              })
            }), _jsx(_components.td, {
              children: "Messages handed off for delivery"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delivered"
              })
            }), _jsx(_components.td, {
              children: "Share that reached the recipient's handset"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clicked"
              })
            }), _jsx(_components.td, {
              children: "Share where the link was clicked"
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["There's no ", _jsx(_components.strong, {
            children: "Opened"
          }), " metric for SMS. Nothing in a text message reports that it was read, so clicks are your engagement signal. Click rate here is a share of messages sent."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "/native-delivery/deliverability",
            children: "Deliverability"
          })
        }), " page has an SMS tab with ", _jsx(_components.strong, {
          children: "Sent"
        }), ", ", _jsx(_components.strong, {
          children: "Delivered"
        }), ", ", _jsx(_components.strong, {
          children: "Send failed"
        }), ", ", _jsx(_components.strong, {
          children: "Clicked"
        }), ", ", _jsx(_components.strong, {
          children: "Subscribed"
        }), ", and ", _jsx(_components.strong, {
          children: "Unsubscribed"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["A high ", _jsx(_components.strong, {
          children: "Quiet hours (no timezone)"
        }), " count means your parent model is missing time zone data."]
      }), "\n", _jsxs(_components.p, {
        children: ["SMS writes to the same event tables. ", _jsx(_components.code, {
          children: "cep_engagement_events"
        }), " holds clicks, with ", _jsx(_components.code, {
          children: "short_id"
        }), " and ", _jsx(_components.code, {
          children: "user_agent"
        }), " on each row. STOP and START land in ", _jsx(_components.code, {
          children: "cep_consent_events"
        }), " alongside every other preference change."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/emails",
            children: "Build a message"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/campaigns",
            children: "Send a campaign"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
